import React, { Component } from 'react';
import styled from 'styled-components';
import compose from 'lodash/fp/compose';
import { withTranslation, withFieldError } from '../../../hoc';
import axios from '../../../../axios';
import API from '../../../../actions/api';

const Wrapper = styled.div`
  margin-bottom: 1.5rem;

  .label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: var(--admincat-color-grey-2);
    
    .required {
      color: red;
      margin-left: 0.25rem;
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border: 1px solid var(--admincat-color-grey-3);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
`;

const ToolbarButton = styled.button.attrs({ type: 'button' })`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--admincat-color-grey-3);
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: var(--admincat-color-grey-2);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;

  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }

  &:active {
    background: #dee2e6;
  }

  &.active {
    background: #4c81be;
    color: white;
    border-color: #4c81be;
  }
`;

const ToolbarSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--admincat-color-grey-3);
  background: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: var(--admincat-color-grey-2);

  &:hover {
    border-color: #adb5bd;
  }
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: var(--admincat-color-grey-3);
  margin: 0 0.25rem;
`;

const EditorArea = styled.div`
  min-height: 300px;
  padding: 1rem;
  border: 1px solid var(--admincat-color-grey-3);
  border-top: none;
  background: white;
  color: #333;
  font-size: 1rem;
  line-height: 1.6;
  overflow-y: auto;
  max-height: 600px;

  &:focus {
    outline: none;
    border-color: var(--admincat-color-primary);
  }

  /* Reset default styles */
  strong, b {
    color: inherit !important;
    font-weight: bold;
  }

  em, i {
    color: inherit !important;
    font-style: italic;
  }

  u {
    color: inherit !important;
    text-decoration: underline;
  }

  /* Placeholder */
  &:empty:before {
    content: attr(data-placeholder);
    color: #999;
    font-style: italic;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ImageUploadModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  min-width: 450px;
  max-width: 90vw;

  h3 {
    margin: 0 0 1.5rem 0;
    color: var(--admincat-color-grey-2);
    font-size: 1.25rem;
  }

  .upload-section {
    margin-bottom: 1.5rem;
  }

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: var(--admincat-color-grey-2);
  }

  input[type="file"] {
    width: 100%;
    padding: 0.75rem;
    border: 2px dashed var(--admincat-color-grey-3);
    border-radius: 4px;
    cursor: pointer;
    background: #f8f9fa;
    
    &:hover {
      border-color: #4c81be;
    }
  }

  input[type="text"] {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--admincat-color-grey-3);
    border-radius: 4px;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #4c81be;
    }
  }

  .divider {
    text-align: center;
    margin: 1rem 0;
    color: #999;
    position: relative;
    
    &::before, &::after {
      content: '';
      position: absolute;
      top: 50%;
      width: 40%;
      height: 1px;
      background: #ddd;
    }
    
    &::before { left: 0; }
    &::after { right: 0; }
  }

  .buttons {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
  }
`;

const ModalButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--admincat-color-grey-3);
  
  &.secondary {
    background: #f8f9fa;
    color: var(--admincat-color-grey-2);

    &:hover {
      background: #e9ecef;
    }
  }

  &.primary {
    background: linear-gradient(135deg, #4c81be 0%, #3a6da3 100%);
    border: none;
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(76, 129, 190, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const PreviewBox = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 4px;
  }
`;

const LinkModal = styled(ImageUploadModal)`
  min-width: 400px;
`;

const HiddenTextarea = styled.textarea`
  position: absolute;
  left: -9999px;
  top: -9999px;
  opacity: 0;
  pointer-events: none;
`;

class BlogRichTextEditor extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      showImageModal: false,
      showLinkModal: false,
      imageUrl: '',
      linkUrl: '',
      linkText: '',
      uploading: false,
      imagePreview: null,
    };

    this.editorRef = React.createRef();
    this.hiddenTextareaRef = React.createRef();
    this.editorId = `blog-editor-${Date.now()}`;
  }

  componentDidMount() {
    if (this.editorRef.current && this.props.value) {
      this.editorRef.current.innerHTML = this.props.value;
    }
    
    // Store editor reference globally for form access
    window[this.editorId] = this;
    
    // Pass the editor instance to parent via callback ref
    if (this.props.editorInstanceRef) {
      this.props.editorInstanceRef(this);
    }
    
    // Legacy support for onEditorReady
    if (this.props.onEditorReady) {
      this.props.onEditorReady(this.getContent);
    }
  }

  componentWillUnmount() {
    delete window[this.editorId];
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      const currentContent = this.editorRef.current?.innerHTML || '';
      const newContent = this.props.value || '';
      
      // Only update if content is truly different (not just from our own changes)
      if (this.normalizeContent(currentContent) !== this.normalizeContent(newContent)) {
        if (this.editorRef.current) {
          this.editorRef.current.innerHTML = newContent;
        }
      }
    }
  }

  normalizeContent = (content) => {
    // Normalize content for comparison
    return (content || '').replace(/\s+/g, ' ').trim();
  };

  getContent = () => {
    if (this.editorRef.current) {
      return this.editorRef.current.innerHTML || '';
    }
    return '';
  };

  setContent = (content) => {
    if (this.editorRef.current) {
      this.editorRef.current.innerHTML = content || '';
      this.syncContent();
    }
  };

  syncContent = () => {
    const content = this.getContent();
    
    // Update hidden textarea
    if (this.hiddenTextareaRef.current) {
      this.hiddenTextareaRef.current.value = content;
    }
    
    // Call onChange with the content
    if (this.props.onChange) {
      this.props.onChange({
        target: {
          name: this.props.name,
          value: content,
        }
      });
    }
  };

  handleInput = () => {
    this.syncContent();
  };

  handleBlur = () => {
    this.syncContent();
  };

  handlePaste = (e) => {
    // Let the paste happen, then sync
    setTimeout(() => {
      this.syncContent();
    }, 0);
  };

  execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (this.editorRef.current) {
      this.editorRef.current.focus();
    }
    this.syncContent();
  };

  handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert(this.props.tr('Please select a valid image file'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert(this.props.tr('Image size should not exceed 5MB'));
      return;
    }

    let capturedImagePreview = null;
    const reader = new FileReader();
    reader.onloadend = () => {
      capturedImagePreview = reader.result;
      this.setState({ imagePreview: reader.result });
    };
    reader.readAsDataURL(file);

    this.setState({ uploading: true });

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(`${API}/blog/upload-image/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      this.insertImage(response.data.url);
      this.setState({ showImageModal: false, uploading: false, imagePreview: null });
    } catch (error) {
      console.error('Upload error:', error);
      if (capturedImagePreview) {
        this.insertImage(capturedImagePreview);
        this.setState({ showImageModal: false, uploading: false, imagePreview: null });
      } else {
        alert(this.props.tr('Image upload failed'));
        this.setState({ uploading: false });
      }
    }
  };

  insertImage = (url) => {
    const img = `<p><img src="${url}" alt="Blog image" style="max-width: 100%; height: auto;" /></p>`;
    if (this.editorRef.current) {
      this.editorRef.current.focus();
    }
    document.execCommand('insertHTML', false, img);
    this.syncContent();
  };

  insertImageFromUrl = () => {
    if (this.state.imageUrl) {
      this.insertImage(this.state.imageUrl);
      this.setState({ showImageModal: false, imageUrl: '', imagePreview: null });
    }
  };

  handleLinkInsert = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    
    this.setState({ 
      showLinkModal: true, 
      linkText: selectedText || '',
      linkUrl: '' 
    });
  };

  insertLink = () => {
    const { linkUrl, linkText } = this.state;
    if (linkUrl) {
      const text = linkText || linkUrl;
      const link = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      if (this.editorRef.current) {
        this.editorRef.current.focus();
      }
      document.execCommand('insertHTML', false, link);
      this.syncContent();
      this.setState({ showLinkModal: false, linkUrl: '', linkText: '' });
    }
  };

  formatBlock = (tag) => {
    document.execCommand('formatBlock', false, tag);
    if (this.editorRef.current) {
      this.editorRef.current.focus();
    }
    this.syncContent();
  };

  render() {
    const { label, required, error, name, tr, placeholder, value } = this.props;
    const { showImageModal, showLinkModal, imageUrl, linkUrl, linkText, uploading, imagePreview } = this.state;

    return (
      <Wrapper>
        {label && (
          <label htmlFor={name} className="label">
            {tr(label || "")}
            {required && <span className="required">*</span>}
          </label>
        )}

        {/* Hidden textarea for form submission */}
        <HiddenTextarea
          ref={this.hiddenTextareaRef}
          name={name}
          id={name}
          defaultValue={value || ''}
          data-editor-id={this.editorId}
        />

        <Toolbar>
          <ToolbarSelect onChange={(e) => { this.formatBlock(e.target.value); e.target.value = ''; }} defaultValue="">
            <option value="" disabled>{tr('Heading')}</option>
            <option value="p">{tr('Paragraph')}</option>
            <option value="h1">{tr('Heading 1')}</option>
            <option value="h2">{tr('Heading 2')}</option>
            <option value="h3">{tr('Heading 3')}</option>
            <option value="h4">{tr('Heading 4')}</option>
          </ToolbarSelect>

          <ToolbarDivider />

          <ToolbarButton onClick={() => this.execCommand('bold')} title={tr('Bold (Ctrl+B)')}>
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('italic')} title={tr('Italic (Ctrl+I)')}>
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('underline')} title={tr('Underline (Ctrl+U)')}>
            <u>U</u>
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('strikeThrough')} title={tr('Strikethrough')}>
            <s>S</s>
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={() => this.execCommand('insertUnorderedList')} title={tr('Bullet List')}>
            • ≡
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('insertOrderedList')} title={tr('Numbered List')}>
            1. ≡
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={() => this.execCommand('justifyLeft')} title={tr('Align Left')}>
            ≡←
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('justifyCenter')} title={tr('Align Center')}>
            ≡↔
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('justifyRight')} title={tr('Align Right')}>
            →≡
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={this.handleLinkInsert} title={tr('Insert Link')}>
            🔗
          </ToolbarButton>
          <ToolbarButton onClick={() => this.setState({ showImageModal: true })} title={tr('Insert Image')}>
            🖼️
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={() => this.execCommand('formatBlock', false, 'blockquote')} title={tr('Quote')}>
            ❝
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('removeFormat')} title={tr('Clear Formatting')}>
            ✕
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton onClick={() => this.execCommand('undo')} title={tr('Undo (Ctrl+Z)')}>
            ↶
          </ToolbarButton>
          <ToolbarButton onClick={() => this.execCommand('redo')} title={tr('Redo (Ctrl+Y)')}>
            ↷
          </ToolbarButton>
        </Toolbar>

        <EditorArea
          ref={this.editorRef}
          contentEditable
          onInput={this.handleInput}
          onBlur={this.handleBlur}
          onPaste={this.handlePaste}
          onKeyUp={this.handleInput}
          data-placeholder={tr(placeholder || 'Start writing your blog post content...')}
          suppressContentEditableWarning={true}
        />

        {error && <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{error}</div>}

        {showImageModal && (
          <>
            <Overlay onClick={() => this.setState({ showImageModal: false, imagePreview: null })} />
            <ImageUploadModal>
              <h3>{tr('Insert Image')}</h3>
              
              <div className="upload-section">
                <label>{tr('Upload from computer:')}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={this.handleImageUpload}
                  disabled={uploading}
                />
              </div>

              {imagePreview && (
                <PreviewBox>
                  <img src={imagePreview} alt="Preview" />
                </PreviewBox>
              )}

              <div className="buttons">
                <ModalButton
                  className="secondary"
                  onClick={() => this.setState({ showImageModal: false, imagePreview: null })}
                  disabled={uploading}
                >
                  {tr('Cancel')}
                </ModalButton>
                {uploading && (
                  <ModalButton className="primary" disabled>
                    {tr('Uploading...')}
                  </ModalButton>
                )}
              </div>
            </ImageUploadModal>
          </>
        )}

        {showLinkModal && (
          <>
            <Overlay onClick={() => this.setState({ showLinkModal: false })} />
            <LinkModal>
              <h3>{tr('Insert Link')}</h3>
              
              <div className="upload-section">
                <label>{tr('Link Text:')}</label>
                <input
                  type="text"
                  placeholder={tr('Display text')}
                  value={linkText}
                  onChange={(e) => this.setState({ linkText: e.target.value })}
                />
              </div>

              <div className="upload-section">
                <label>{tr('URL:')}</label>
                <input
                  type="text"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => this.setState({ linkUrl: e.target.value })}
                />
              </div>

              <div className="buttons">
                <ModalButton
                  className="secondary"
                  onClick={() => this.setState({ showLinkModal: false, linkUrl: '', linkText: '' })}
                >
                  {tr('Cancel')}
                </ModalButton>
                <ModalButton
                  className="primary"
                  onClick={this.insertLink}
                  disabled={!linkUrl}
                >
                  {tr('Insert Link')}
                </ModalButton>
              </div>
            </LinkModal>
          </>
        )}
      </Wrapper>
    );
  }
}

export default compose(withFieldError, withTranslation)(BlogRichTextEditor);